// Pump displacement — deterministic auto-calc helper.
//
// Pure module. No side effects, no I/O. Used by the Pump Profile form and
// the kill sheet snapshot embedding step.
//
// Industry-standard formulas (e.g. IADC Drilling Manual, SPE references):
//
//   Triplex single-acting (3 cylinders, suction-discharge per stroke):
//     Imperial: disp_bbl = 0.000243 × L² × S × (η / 100)
//                          where L = liner Ø (in), S = stroke (in), η % efficiency
//                          Constant 0.000243 = 3 × π/4 ÷ 9702 (3 cyl built in).
//
//     Metric:   disp_m3  = (3π/4) × (L/1000)² × (S/1000) × (η/100)
//                          = 2.3562e-9 × L² × S × η/100, L,S in mm.
//
//   Duplex double-acting (2 cylinders, suction-discharge BOTH sides of piston):
//     Imperial: disp_bbl = 0.000162 × S × (2L² − R²) × (η/100)
//                          where R = rod Ø (in). Constant 0.000162 = π/2 ÷ 9702.
//
//     Metric:   disp_m3  = (π/2) × (S/1000) × ((L/1000)² × 2 − (R/1000)²) × (η/100)
//                          = 1.5708e-9 × S × (2L² − R²) × η/100, L,R,S in mm.
//
// All inputs may be strings (form-edited). Any missing/non-numeric input
// returns null — the form falls back to legacy `displacement` if present.

const _num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const _pos = (v) => {
  const n = _num(v);
  return n != null && n > 0 ? n : null;
};

export const PUMP_TYPES = ["triplex_sa", "duplex_da"];
export const PUMP_TYPE_LABELS = {
  triplex_sa: "Triplex (single-acting)",
  duplex_da:  "Duplex (double-acting)",
};

export const DEFAULT_PUMP_PRESETS = {
  triplex_sa: { efficiency: "97", cylinders: "3" },
  duplex_da:  { efficiency: "90", cylinders: "2" },
};

/**
 * Compute pump displacement from triplex/duplex geometry.
 *
 * @param {object} pump   {pumpType, liner, stroke, rodDia, efficiency}
 * @param {string} units  "IMP" | "SI"
 * @returns {number|null} bbl/stk (IMP) or m³/stk (SI), or null if missing data.
 */
export function computePumpDisplacement(pump, units) {
  if (!pump || typeof pump !== "object") return null;
  const L = _pos(pump.liner);
  const S = _pos(pump.stroke);
  const E = _num(pump.efficiency);
  if (L == null || S == null || E == null) return null;
  if (E <= 0 || E > 100) return null;
  const eFrac = E / 100;

  const pumpType = pump.pumpType || "triplex_sa";

  if (pumpType === "triplex_sa") {
    if (units === "SI") {
      return 2.3562e-9 * L * L * S * eFrac;
    }
    return 0.000243 * L * L * S * eFrac;
  }

  if (pumpType === "duplex_da") {
    const R = _num(pump.rodDia);
    if (R == null) return null;
    if (units === "SI") {
      return 1.5708e-9 * S * (2 * L * L - R * R) * eFrac;
    }
    return 0.000162 * S * (2 * L * L - R * R) * eFrac;
  }

  return null;
}

/**
 * Resolve effective pump displacement: prefer derived from geometry if
 * present, fall back to user-stored legacy `displacement` value. Returns
 * a string (form/number value) or "" if nothing is derivable.
 */
export function resolveDisplacement(pump, units) {
  const derived = computePumpDisplacement(pump, units);
  if (derived != null) {
    const digits = units === "SI" ? 6 : 4;
    return String(Number(derived.toFixed(digits)));
  }
  return pump?.displacement ?? "";
}

/**
 * Pump calibration variance — operational confidence check (P3).
 *
 * Given a measured stroke count and a measured discharged volume taken
 * during a slow-pump verification, compute the *actual* per-stroke
 * displacement and compare it to the derived value.
 *
 * Inputs:
 *   pump.measuredStrokes  — integer count of strokes pumped
 *   pump.measuredVolume   — volume discharged (bbl for IMP, m³ for SI)
 *
 * Returns:
 *   { ok: true, actual, derived, variancePct, status }   when both inputs valid
 *   { ok: false }                                         otherwise
 *
 *   variancePct is signed: positive = actual greater than derived.
 *   status ∈ "green" | "amber" | "red":
 *     green : |variancePct| ≤ 2
 *     amber : 2 < |variancePct| ≤ 5
 *     red   : |variancePct| > 5
 *
 * Pure function — no I/O, no side effects. Safe to call from any layer.
 */
export function calibrationStatus(variancePct) {
  if (variancePct == null || !Number.isFinite(variancePct)) return null;
  const abs = Math.abs(variancePct);
  if (abs <= 2) return "green";
  if (abs <= 5) return "amber";
  return "red";
}

export function pumpCalibration(pump, units) {
  if (!pump || typeof pump !== "object") return { ok: false };
  const strokes = _pos(pump.measuredStrokes);
  const volume  = _pos(pump.measuredVolume);
  if (strokes == null || volume == null) return { ok: false };
  const actual  = volume / strokes;
  const derived = computePumpDisplacement(pump, units);
  if (derived == null || derived <= 0) {
    return { ok: false, actual };
  }
  const variancePct = ((actual - derived) / derived) * 100;
  return {
    ok: true,
    actual,
    derived,
    variancePct,
    status: calibrationStatus(variancePct),
  };
}

